import React from 'react';
import { motion } from 'framer-motion';
import { ShieldCheck, Check, ArrowRight } from 'lucide-react';
import { trackAddToCart } from '../utils/metaPixel';
export function PricingSection() {
  return <section id="pricing" className="py-24 bg-blush-50 relative" data-id="element-147">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-148">
        {/* Guarantee Badge */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.9
      }} whileInView={{
        opacity: 1,
        scale: 1
      }} viewport={{
        once: true
      }} className="max-w-3xl mx-auto bg-white rounded-3xl p-8 md:p-12 shadow-xl border border-blush-200 text-center mb-24 relative overflow-hidden" data-id="element-149">
          <div className="absolute top-0 left-0 w-full h-2 bg-gold-400" data-id="element-150"></div>
          <ShieldCheck className="w-16 h-16 text-gold-500 mx-auto mb-6" data-id="element-151" />
          <h2 className="text-3xl font-serif font-bold text-ink-900 mb-4" data-id="element-152">
            GARANTÍA DE 7 DÍAS
          </h2>
          <p className="text-2xl font-serif italic text-emerald-900 mb-6" data-id="element-153">
            "Si no te convence, te devuelvo tu dinero."
          </p>
          <p className="text-ink-800 mb-6 leading-relaxed" data-id="element-154">
            Estoy tan segura de que este método funciona que te doy{' '}
            <strong data-id="element-155">7 días completos</strong> para comprobarlo por ti misma.
            Compra. Descarga. Aplica. Vende.
          </p>
          <p className="text-ink-800 mb-6 leading-relaxed" data-id="element-156">
            Si después de leerlo y armar al menos un kit sientes que no fue lo
            que esperabas — escríbeme dentro de los primeros 7 días y te
            devuelvo el 100% de tu inversión. Sin letra pequeña. Sin condiciones
            trampa.
          </p>
          <div className="bg-blush-50 p-4 rounded-xl text-sm text-ink-800/80 italic" data-id="element-157">
            💡 ¿Por qué solo 7 días? Porque este método está diseñado para dar
            resultados en 48 horas — no necesitas más tiempo para saber si
            funciona. Y si lo aplicas, vas a querer quedártelo.
          </div>
        </motion.div>

        {/* Urgency */}
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-158">
          <h2 className="text-3xl md:text-4xl font-bold text-ink-900 mb-6" data-id="element-159">
            ⏰ PERO HAY ALGO MÁS QUE NECESITAS SABER...
          </h2>
          <p className="text-xl text-ink-800 mb-4" data-id="element-160">
            Mayo tiene fecha límite. El Día de la Madre{' '}
            <strong data-id="element-161">no espera a nadie.</strong>
          </p>
          <p className="text-lg text-ink-800 mb-8" data-id="element-162">
            La oportunidad de Mayo 2025 solo existe UNA VEZ. La pregunta no es
            si puedes hacerlo. Ya sabes que puedes. La pregunta es:{' '}
            <strong data-id="element-163">
              ¿Lo harás hoy, o dejarás que este Mayo pase como todos los
              anteriores?
            </strong>
          </p>
        </div>

        {/* Value Stack */}
        <div className="text-center mb-12" data-id="element-164">
          <h3 className="text-2xl font-serif font-bold text-ink-900 mb-6" data-id="element-165">
            💎 TU INVERSIÓN HOY
          </h3>
          <div className="inline-block space-y-2 mb-8" data-id="element-166">
            <p className="text-ink-800/50 line-through text-lg" data-id="element-167">
              Valor Total: $106.00
            </p>
            <p className="text-ink-800/50 line-through text-lg" data-id="element-168">
              Precio Regular: $47.00
            </p>
            <p className="text-emerald-900 font-bold text-2xl mt-4" data-id="element-169">
              Oferta Especial de Lanzamiento:{' '}
              <span className="text-4xl" data-id="element-170">$9.97</span>
            </p>
            <p className="text-gold-500 font-bold text-lg mt-2" data-id="element-171">
              (Ahorras un 75% hoy)
            </p>
          </div>
        </div>

        {/* Single Pricing Card */}
        <div className="max-w-lg mx-auto" data-id="element-172">
          <motion.div id="pricing-card" whileHover={{
          y: -5
        }} className="bg-white rounded-3xl p-8 md:p-10 border-2 border-emerald-900 shadow-2xl flex flex-col relative overflow-hidden" data-id="element-173">
            <div className="absolute top-0 left-0 w-full h-2 bg-emerald-900" data-id="element-174"></div>

            <div className="text-center mb-8" data-id="element-175">
              <span className="inline-block px-5 py-1.5 bg-emerald-900/10 text-emerald-900 font-bold text-sm tracking-widest uppercase mb-4 rounded-full" data-id="element-176">
                OFERTA DE LANZAMIENTO
              </span>
              <div className="text-7xl font-black text-ink-900 mb-2" data-id="element-177">$9.97</div>
              <p className="text-ink-800/70 italic" data-id="element-178">
                Sí. El precio de una vela en Bath & Body Works.
              </p>
            </div>

            <div className="flex-grow" data-id="element-179">
              <p className="font-bold text-ink-900 mb-4 flex items-center" data-id="element-180">
                <span className="mr-2" data-id="element-181">🎁</span> Incluye:
              </p>
              <ul className="space-y-4 mb-8" data-id="element-182">
                {['Ebook completo — 7 capítulos (PDF)', 'Plantilla 1: Lista de Compras Maestra', 'Plantilla 2: Calculadora de Gastos y Ganancias', 'Plantilla 3: Control de Ventas', 'Acceso de por vida a tus descargas', 'Garantía de 7 días'].map((feature, i) => <li key={i} className="flex items-start" data-id="element-183">
                    <Check className="w-5 h-5 text-emerald-800 mr-3 flex-shrink-0 mt-0.5" data-id="element-184" />
                    <span className="text-ink-800" data-id="element-185">{feature}</span>
                  </li>)}
              </ul>
            </div>

            <a href="https://nuvikali.com/cart/45307846459494:1" target="_blank" rel="noopener noreferrer" onClick={() => trackAddToCart()} className="w-full py-4 bg-emerald-900 text-white rounded-xl font-bold text-lg hover:bg-emerald-800 transition-colors shadow-lg flex justify-center items-center space-x-2 no-underline" data-id="element-186">
              <span data-id="element-187">QUIERO EL MÉTODO 3-3-3 AHORA — $9.97</span>
              <ArrowRight className="w-5 h-5" data-id="element-188" />
            </a>
            <p className="text-center text-xs text-ink-800/50 mt-4" data-id="element-189">
              Acceso inmediato · Descarga instantánea · Garantía 7 días
            </p>
          </motion.div>
        </div>

        {/* Final CTA & Footer */}
        <div className="mt-32 text-center max-w-2xl mx-auto" data-id="element-190">
          <h2 className="text-3xl font-serif font-bold text-ink-900 mb-6" data-id="element-191">
            UN ÚLTIMO PENSAMIENTO...
          </h2>
          <p className="text-lg text-ink-800 mb-4" data-id="element-192">
            Hay dos tipos de personas que leen esto.
          </p>
          <p className="text-lg text-ink-800 mb-4" data-id="element-193">
            <strong data-id="element-194">Las que dicen:</strong>{' '}
            <em data-id="element-195">"Qué interesante... lo pienso."</em> Y dos semanas después,
            cuando el tiempo ya se acaba, piensan:{' '}
            <em data-id="element-196">"Debí haberlo comprado cuando lo vi."</em>
          </p>
          <p className="text-lg text-ink-800 mb-8" data-id="element-197">
            <strong data-id="element-198">Y las que dicen:</strong>{' '}
            <em data-id="element-199">"Esto es exactamente lo que necesito. Lo hago ahora."</em> Y el
            siguiente Día de la Madre tienen una historia que contar. Un ingreso
            que celebrar.
          </p>

          <h3 className="text-2xl font-bold text-emerald-900 mb-8" data-id="element-200">
            ¿Cuál de las dos serás tú?
          </h3>

          <button className="w-full sm:w-auto px-8 py-5 bg-emerald-900 text-white rounded-full text-xl font-bold shadow-xl hover:bg-emerald-800 transition-transform hover:scale-105 mb-16" data-id="element-201">
            SÍ, QUIERO TRANSFORMAR $15 EN $45
          </button>

          <div className="border-t border-blush-200 pt-12 pb-8" data-id="element-202">
            <p className="font-serif italic text-ink-800 mb-8" data-id="element-203">
              Con amor, estrategia y muchos listones de terciopelo 🎀
            </p>
            <p className="text-sm text-ink-800/60 font-bold mb-2" data-id="element-204">
              © MÉTODO 3-3-3 — Emprendimiento Express
            </p>
            <p className="text-sm text-ink-800/60 mb-6" data-id="element-205">
              Especial Día de la Madre • Garantía de 7 días • Descarga inmediata
            </p>
            <p className="text-xs text-ink-800/40 italic max-w-md mx-auto" data-id="element-206">
              "Sin máquinas costosas, sin inventarios infinitos: Solo tú, 3
              tiendas locales... y el Mayo que siempre mereciste." ✨
            </p>
          </div>
        </div>
      </div>
    </section>;
}